import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
  Stack,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import Cropper from "react-easy-crop";

/**
 * Props:
 * - open: boolean
 * - onClose: () => void
 * - onSave: (blob, fileName) => Promise|void   // blob PNG del recorte
 * - onRemove: () => Promise|void               // remover avatar existente
 * - initialImageUrl?: string                   // URL actual del avatar (opcional)
 */
export default function AvatarUploadDialog({
  open,
  onClose,
  onSave,
  onRemove,
  initialImageUrl,
}) {
  const [imageSrc, setImageSrc] = React.useState(initialImageUrl || null);
  const [crop, setCrop] = React.useState({ x: 0, y: 0 });
  const [zoom, setZoom] = React.useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState("");
  
  const inputRef = React.useRef(null);

  React.useEffect(() => {
    if (!open) {
      setImageSrc(initialImageUrl || null);
      setError("");
      setZoom(1);
      setCrop({ x: 0, y: 0 });
      setCroppedAreaPixels(null);
      setBusy(false);
    }
  }, [open, initialImageUrl]);

  const onSelectFile = (e) => {
    setError("");
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;

    const okTypes = ["image/png", "image/jpeg", "image/jpg", "image/webp"];
    if (!okTypes.includes(file.type)) {
      setError("Formato no soportado. Usa PNG o JPG.");
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      setError("La imagen supera 8MB.");
      return;
    }

    const reader = new FileReader();
    reader.onload = () => setImageSrc(reader.result);
    reader.readAsDataURL(file);
  };

  const onCropComplete = React.useCallback((_, croppedPixels) => {
    setCroppedAreaPixels(croppedPixels);
  }, []);

  const handleSave = async () => {
    if (!imageSrc || !croppedAreaPixels) {
      setError("Selecciona una imagen y ajusta el recorte.");
      return;
    }
    setBusy(true);
    try {
      const blob = await getCroppedImageBlob(imageSrc, croppedAreaPixels);
      const fileName = `avatar_${Date.now()}.png`;
      await onSave?.(blob, fileName);
      onClose?.();
    } catch (e) {
      console.error(e);
      setError("No se pudo generar el recorte.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onClose}
      fullWidth
      maxWidth="sm"
      sx={{
        "& .MuiPaper-root": {
          borderRadius: "10px",
          backgroundColor: "#1E1B2E",
          border: "1px solid rgba(157,0,255,0.35)",
          color: "#EDE9F7",
        },
      }}
    >
      <DialogTitle sx={{ p: 2.5 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography sx={{ fontWeight: 800 }}>Upload New Avatar</Typography>
          <IconButton size="small" onClick={onClose} disabled={busy}>
            <CloseIcon sx={{ color: "#EDE9F7" }} />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent dividers sx={{ borderColor: "rgba(237,233,247,0.08)" }}>
        <Typography variant="body2" sx={{ opacity: 0.8, mb: 1 }}>
          Your image must be a PNG or JPG. PNGs are recommended as they produce a lossless image.
        </Typography>
        <Typography variant="body2" sx={{ opacity: 0.8, mb: 2 }}>
          The recommended size for an avatar is <b>1000×1000</b>.
        </Typography>

        <Button
          variant="outlined"
          onClick={() => inputRef.current?.click()}
          sx={{
            color: "#C77DFF",
            borderColor: "#9D00FF",
            textTransform: "none",
            fontWeight: 700,
            mb: 2,
            "&:hover": { borderColor: "#C77DFF" },
          }}
          disabled={busy}
        >
          Choose File
        </Button>
        <input
          ref={inputRef}
          type="file"
          accept="image/png,image/jpeg,image/jpg,image/webp"
          style={{ display: "none" }}
          onChange={onSelectFile}
        />

        <Typography sx={{ mt: 2, mb: 1, fontWeight: 800 }}>
          Your Uploaded Avatar
        </Typography>

        <Box
          sx={{
            position: "relative",
            height: 360,
            background: "#14141F",
            borderRadius: 1.5,
            overflow: "hidden",
            border: "1px solid rgba(237,233,247,0.08)",
          }}
        >
          {imageSrc ? (
            <Cropper
              image={imageSrc}
              crop={crop}
              zoom={zoom}
              aspect={1}
              showGrid={false}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
              objectFit="contain"
              restrictPosition
            />
          ) : (
            <Box
              sx={{
                width: "100%",
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#A8A3B8",
                fontStyle: "italic",
              }}
            >
              No image selected
            </Box>
          )}
        </Box>

        {error && (
          <Typography sx={{ color: "#FF5C5C", mt: 1 }}>{error}</Typography>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, gap: 1.5 }}>
        <Button
          variant="outlined"
          onClick={onRemove}
          disabled={busy}
          sx={{
            borderColor: "#9D00FF",
            color: "#C77DFF",
            textTransform: "none",
            fontWeight: 700,
            "&:hover": { borderColor: "#C77DFF", color: "#C77DFF" },
          }}
        >
          Remove Avatar
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button
          onClick={onClose}
          disabled={busy}
          sx={{
            color: "#EDE9F7",
            textTransform: "none",
            fontWeight: 700,
            "&:hover": { background: "rgba(157,0,255,.08)" },
          }}
        >
          Close
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={!imageSrc || busy}
          sx={{
            background: "#6A00CC",
            color: "#EDE9F7",
            textTransform: "none",
            fontWeight: 800,
            "&:hover": { background: "#9D00FF" },
          }}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/* -------------------------------
   Util: genera un Blob PNG recortado
----------------------------------*/
async function getCroppedImageBlob(imageSrc, cropPixels) {
  const image = await loadImage(imageSrc);
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");

  const { x, y, width, height } = cropPixels;

  canvas.width = width;
  canvas.height = height;

  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(
    image,
    x, y, width, height, // src
    0, 0, width, height  // dest
  );

  return await new Promise((resolve) =>
    canvas.toBlob((blob) => resolve(blob), "image/png", 1)
  );
}

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.addEventListener("load", () => resolve(img));
    img.addEventListener("error", reject);
    img.crossOrigin = "anonymous";
    img.src = src;
  });
}
